import { useState, useEffect, useRef } from 'react';
import './index.css';
import { supabase, getStudentsByParent, resolveSession, signInWithGoogle, signInWithEmail, signOut, setPasswordComplete, markStudentPasswordSet } from './lib/supabase';
import PinScreen from './components/PinScreen';
import StudentPlanner from './pages/StudentPlanner';
import AdminPanel from './pages/AdminPanel';
import roundelSrc from './assets/roundel.svg';

// ── MODES ────────────────────────────────────────────────────
// 1. /admin  → Google or email/password login → parent/teacher dashboard,
//              or (when the session resolves to a student) their planner
// 2. /       → landing / login prompt, except that a signed-in student is
//              routed straight to their planner (resolved from the session)

// ── SIGNED-IN STUDENT PIN LOCK ───────────────────────────────
// A signed-in student's planner locks behind their PIN like a phone screen —
// the Supabase session stays alive, only the view is gated. The PIN is
// cosmetic by design: it stops a sibling picking up the laptop, and the
// database never trusts it. The unlock is remembered for 24 hours, because
// that is the behaviour Finn already knows.

const AUTH_PIN_TTL_MS = 86400000; // 24 hours

function authPinKey(studentId) {
  return 'auth_pin_verified_' + studentId;
}

function isAuthPinValid(studentId) {
  const stamp = localStorage.getItem(authPinKey(studentId));
  return !!stamp && Date.now() - parseInt(stamp, 10) < AUTH_PIN_TTL_MS;
}

export default function App() {
  const [mode, setMode] = useState(null); // 'admin' | 'landing'
  const [student, setStudent] = useState(null);
  const [session, setSession] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [, setIsAdmin] = useState(false); // value never read (the linter found this); setter calls left as they were
  const [loading, setLoading] = useState(true);
  const [viewingPlanner, setViewingPlanner] = useState(false);
  const [authError, setAuthError] = useState('');
  const [needsPassword, setNeedsPassword] = useState(null); // null | 'teacher' | 'student'
  const [authStudent, setAuthStudent] = useState(null);     // student recognised from the session itself
  const [accessDenied, setAccessDenied] = useState(null);   // reason string when an account matches nothing
  const [resolving, setResolving] = useState(false);        // working out who a signed-in account is
  const [authStudentLocked, setAuthStudentLocked] = useState(true); // planner gated behind the PIN until unlocked

  // Who this tab is signed in as, and what we already know about them. Refs,
  // not state: the auth listener below is set up once and would only ever see
  // the first render's state.
  const currentUserId = useRef(null); // the session's user id, or null
  const recognised = useRef(null);    // { userId, studentId } once recognised
  const inflight = useRef(null);      // { userId, promise } while a pass runs
  const pass = useRef(0);             // bumped by every new pass and by forgetting

  useEffect(() => {
    init();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session) handleSession(session);
      else if (event === 'SIGNED_OUT') forgetIdentity(); // here or in another tab
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only on purpose: init() and the auth listener must be set up exactly once
  }, []);

  async function init() {
    const path = window.location.pathname;

    if (path === '/admin' || path.startsWith('/admin')) {
      setMode('admin');
      const { data } = await supabase.auth.getSession();
      if (data.session) await handleSession(data.session);
    } else {
      setMode('landing');
      // A signed-in student lands on their planner from the session itself —
      // resolve before we drop the loading screen so they never see landing.
      const { data } = await supabase.auth.getSession();
      if (data.session) await handleSession(data.session);
    }
    setLoading(false);
  }

  // One recognition per signed-in person. init()'s getSession, the listener's
  // INITIAL_SESSION and the SIGNED_IN after Google's redirect arrive together
  // and share one pass. Later events for the same person (a tab coming back,
  // the hourly token refresh, a password change) change nothing but the
  // session: no re-read, no loading screen, no page reset. A different person,
  // or an earlier answer of "nobody", is recognised afresh.
  function handleSession(session) {
    const userId = session.user.id;
    if (currentUserId.current !== userId) {
      if (currentUserId.current) forgetIdentity(); // someone else in this browser now
      currentUserId.current = userId;
    }
    const known = recognised.current;
    if (known?.userId === userId) {
      // Still checked on every event: a student's 24-hour unlock, so an
      // expired one locks on return as it always has.
      if (known.studentId) setAuthStudentLocked(!isAuthPinValid(known.studentId));
      return Promise.resolve();
    }
    if (inflight.current?.userId === userId) return inflight.current.promise;
    const promise = recognise(session);
    inflight.current = { userId, promise };
    return promise;
  }

  async function recognise(session) {
    const myPass = ++pass.current;
    const userId = session.user.id;
    // Signed out, or someone else signed in, while we were reading: an answer
    // for a session this tab no longer holds is thrown away, never applied.
    const stale = () => pass.current !== myPass;
    setResolving(true);
    try {
      const result = await resolveSession(session);
      if (stale()) return;

      // Student — straight to their planner. Never a users record, never admin.
      if (result.kind === 'student') {
        recognised.current = { userId, studentId: result.student.id };
        setAuthStudent(result.student);
        // One rule, no special cases: the planner shows only while a valid
        // unlock is on record. That includes right after an interactive
        // sign-in — predictable beats clever.
        setAuthStudentLocked(!isAuthPinValid(result.student.id));
        setDbUser(null);
        setIsAdmin(false);
        setAccessDenied(null);
        setNeedsPassword(result.student.password_set === false ? 'student' : null);
        return;
      }

      // Recognised by nothing — polite dead end, and nothing is created anywhere.
      // Not remembered, so the next auth event looks again (reads only).
      if (result.kind === 'none') {
        recognised.current = null;
        setAuthStudent(null);
        setDbUser(null);
        setIsAdmin(false);
        setNeedsPassword(null);
        setAccessDenied(result.reason || 'unknown');
        return;
      }

      const u = result.user;
      setAuthStudent(null);
      setAccessDenied(null);
      setDbUser(u);

      // Check if teacher needs to set a password
      if (u.role === 'teacher' && u.password_set === false) {
        recognised.current = { userId, studentId: null };
        setNeedsPassword('teacher');
        setMode('admin');
        return;
      }
      setNeedsPassword(null);

      if (u.role === 'parent') {
        setIsAdmin(true);
        const existingStudents = await getStudentsByParent(u.id);
        if (stale()) return;
        if (existingStudents.length > 0) setStudent(existingStudents[0]);
      }
      recognised.current = { userId, studentId: null };
    } catch (e) {
      if (stale()) return;
      // Never fall through to a guessed identity — fail closed.
      console.error('handleSession:', e);
      recognised.current = null;
      setAuthStudent(null);
      setDbUser(null);
      setIsAdmin(false);
      setNeedsPassword(null);
      setAccessDenied('error');
    } finally {
      if (!stale()) {
        inflight.current = null;
        setResolving(false);
      }
    }
  }

  // Forgets whoever was signed in: the recognised person, any pass still in
  // flight, and every piece of identity state, including the selected child.
  // Safe to run twice: the tab that signs out receives its own SIGNED_OUT too.
  function forgetIdentity() {
    const known = recognised.current;
    if (known?.studentId) localStorage.removeItem(authPinKey(known.studentId));
    currentUserId.current = null;
    recognised.current = null;
    inflight.current = null;
    pass.current++;
    setResolving(false);
    setAuthStudentLocked(true);
    setDbUser(null);
    setIsAdmin(false);
    setViewingPlanner(false);
    setNeedsPassword(null);
    setAuthStudent(null);
    setAccessDenied(null);
    setStudent(null);
  }

  async function handleSignIn() {
    setAuthError('');
    try {
      await signInWithGoogle();
    } catch (e) {
      setAuthError('Sign-in failed — please try again.');
    }
  }

  async function handleEmailSignIn(email, password) {
    setAuthError('');
    try {
      await signInWithEmail(email, password);
      // onAuthStateChange will fire and handleSession will run
    } catch (e) {
      setAuthError(e.message || 'Sign-in failed — check your email and password.');
    }
  }

  async function handleSignOut() {
    // A signed-out account must never leave a stale unlock behind for whoever
    // signs in next.
    if (authStudent) localStorage.removeItem(authPinKey(authStudent.id));
    await signOut();
    setSession(null);
    forgetIdentity();
  }

  async function handlePasswordComplete() {
    try {
      // The flag lives on the student's own row for students, on the users
      // row for teachers.
      if (needsPassword === 'student' && authStudent) {
        const updated = await markStudentPasswordSet();
        setAuthStudent(updated || { ...authStudent, password_set: true });
      } else if (dbUser) {
        await setPasswordComplete(dbUser.id);
        setDbUser({ ...dbUser, password_set: true });
      }
      setNeedsPassword(null);
    } catch (e) {
      console.error('handlePasswordComplete:', e);
    }
  }

  if (loading) return <LoadingScreen />;

  // ── SIGNED-IN ACCOUNT WE DON'T RECOGNISE ─────────────────
  if (session && accessDenied) {
    return <NoAccessScreen email={session.user?.email} onSignOut={handleSignOut} />;
  }

  // ── SIGNED-IN STUDENT ────────────────────────────────────
  // Recognised from the session, no token in the URL.
  if (session && authStudent) {
    // Password first, then PIN — a student forced to set a password shouldn't
    // need to get past a PIN to do it.
    if (needsPassword === 'student') {
      return <SetPasswordScreen onComplete={handlePasswordComplete} />;
    }
    if (authStudentLocked) {
      return (
        <PinScreen
          student={authStudent}
          storageKey={authPinKey(authStudent.id)}
          onSuccess={() => setAuthStudentLocked(false)}
          onFullSignOut={handleSignOut}
        />
      );
    }
    return (
      <StudentPlanner
        student={authStudent}
        isAdmin={false}
        isStudent={true}
        userId={null}
        studentDbId={authStudent.id}
        onSignOut={null}
        onStudentSignOut={() => {
          // "Way out" locks the planner — it never signs out. The school
          // laptop stays signed in; only the PIN gate comes back.
          localStorage.removeItem(authPinKey(authStudent.id));
          setAuthStudentLocked(true);
        }}
      />
    );
  }

  // ── ADMIN MODE ───────────────────────────────────────────
  if (mode === 'admin') {

    // First-time password setup for invited teachers
    if (needsPassword === 'teacher' && session) {
      return (
        <SetPasswordScreen
          onComplete={handlePasswordComplete}
        />
      );
    }

    if (!session) {
      return (
        <LoginScreen
          onGoogleSignIn={handleSignIn}
          onEmailSignIn={handleEmailSignIn}
          authError={authError}
        />
      );
    }

    // Signed in but not identified yet — wait rather than guess a role.
    if (resolving || !dbUser) return <LoadingScreen />;

    if (viewingPlanner && student) {
      const isTeacherUser = dbUser?.role === 'teacher';
      return (
        <StudentPlanner
          student={student}
          isAdmin={true}
          isTeacher={isTeacherUser}
          userId={dbUser?.id}
          studentDbId={student?.id}
          onBack={() => setViewingPlanner(false)}
          onSignOut={() => { setViewingPlanner(false); handleSignOut(); }}
        />
      );
    }

    return (
      <AdminPanel
        user={dbUser}
        student={student}
        setStudent={setStudent}
        onViewPlanner={() => setViewingPlanner(true)}
        onSignOut={handleSignOut}
      />
    );
  }

  // ── LANDING ──────────────────────────────────────────────
  return (
    <div className="login-screen">
      <RoundelImg size={80} />
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>HOMEWORK UNDERGROUND</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 300 }}>
          Assignment planner for students with synced reminders for parents and teachers.
        </p>
      </div>
      <a href="/admin" style={{ textDecoration: 'none' }}>
        <button className="btn btn-primary" style={{ fontSize: 15 }}>
          Sign in →
        </button>
      </a>
    </div>
  );
}

// ── SHARED ROUNDEL COMPONENT ──────────────────────────────────

function RoundelImg({ size = 80, style = {} }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <svg style={{ width: size, height: size, ...style }} viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="38" fill="#e32017"/>
        <rect x="2" y="29" width="76" height="22" fill="#003688"/>
        <text x="40" y="44" textAnchor="middle" fontSize="12" fill="white" fontWeight="700" fontFamily="Arial">HOMEWORK</text>
      </svg>
    );
  }
  return (
    <img
      src={roundelSrc}
      alt="Homework Underground"
      width={size}
      height={size}
      style={{ display: 'block', ...style }}
      onError={() => setFailed(true)}
    />
  );
}

// ── LOGIN SCREEN ──────────────────────────────────────────────

function LoginScreen({ onGoogleSignIn, onEmailSignIn, authError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!email || !password) return;
    setSubmitting(true);
    await onEmailSignIn(email, password);
    setSubmitting(false);
  }

  return (
    <div className="login-screen">
      <RoundelImg size={80} />
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>HOMEWORK UNDERGROUND</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>Sign in to your planner</p>
      </div>

      <button className="google-btn" onClick={onGoogleSignIn}>
        <GoogleIcon />
        Sign in with Google
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', maxWidth: 320, color: 'var(--muted)', fontSize: 12 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span>OR</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="email"
          style={{ padding: '12px 14px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, background: 'var(--bg)', color: 'var(--text)' }}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          autoComplete="current-password"
          style={{ padding: '12px 14px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, background: 'var(--bg)', color: 'var(--text)' }}
        />
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !email || !password}
          style={{ fontSize: 15, padding: '12px' }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </div>

      {authError && <div style={{ color: 'var(--red)', fontSize: 13, textAlign: 'center', maxWidth: 320 }}>{authError}</div>}
    </div>
  );
}

// ── NO-ACCESS SCREEN ──────────────────────────────────────────
// Where every account we don't recognise ends up. Nothing is created for
// them anywhere — they just get a polite dead end and a way back out.

function NoAccessScreen({ email, onSignOut }) {
  return (
    <div className="login-screen">
      <RoundelImg size={80} />
      <div style={{ textAlign: 'center', maxWidth: 340 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>MIND THE GAP</h1>
        <p style={{ color: 'var(--muted)', fontSize: 15 }}>
          This account isn't set up yet — ask a parent to add you.
        </p>
        {email && (
          <p style={{ color: 'var(--muted)', fontSize: 13, opacity: 0.7, marginTop: 10 }}>
            Signed in as {email}
          </p>
        )}
      </div>
      <button className="btn btn-primary" onClick={onSignOut} style={{ fontSize: 15 }}>
        Sign out & try another account
      </button>
    </div>
  );
}

// ── SET PASSWORD SCREEN ───────────────────────────────────────

function SetPasswordScreen({ onComplete }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSetPassword() {
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSaving(true);
    setError('');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setError(error.message); setSaving(false); return; }
    onComplete();
  }

  return (
    <div className="login-screen">
      <RoundelImg size={80} />
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Choose your NEW password</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 320 }}>
          Don't re-enter the temporary password you were given — pick a new one only you know.
        </p>
      </div>
      <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="password"
          placeholder="New password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          style={{ padding: '12px 14px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, background: 'var(--bg)', color: 'var(--text)' }}
        />
        <input
          type="password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
          style={{ padding: '12px 14px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, background: 'var(--bg)', color: 'var(--text)' }}
        />
        {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
        <button
          className="btn btn-primary"
          onClick={handleSetPassword}
          disabled={saving}
          style={{ fontSize: 15, padding: '12px' }}
        >
          {saving ? 'Setting password…' : 'Set password & continue →'}
        </button>
      </div>
    </div>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#003688' }}>
      <RoundelImg size={72} style={{ opacity: 0.9 }} />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
